import React from 'react';
import { ModalActions } from '../Modal/Modal';
import StepProgress from '../StepProgress/StepProgress';
import styles from './EnrollPictureScreen.module.scss';
import { SUPPORT_EMAIL } from '@/rescue/lib/emails';

interface EnrollPictureScreenProps {
  heroSrc: string;
  heroAlt?: string;
  /** Extra class on the hero <img>, for cards whose photo needs a different crop than centred. */
  heroClassName?: string;
  onClose: () => void;
  title: string;
  description: React.ReactNode;
  currentStep: number;
  totalSteps: number;
  /** Passed through to <StepProgress>: when true, past dots render gray instead of complete. */
  highlightCurrentOnly?: boolean;
  actions: React.ReactNode;
}

/**
 * Picture-cover screen used inside enrollment modals: hero image + title +
 * description + step progress + action buttons + support link. Render inside a
 * `<Modal size="extra-wide" showCloseX={false}>` and pass the shared `modal` /
 * `backdrop` classes from this module to the Modal wrapper.
 */
export default function EnrollPictureScreen({
  heroSrc,
  heroAlt,
  heroClassName,
  onClose,
  title,
  description,
  currentStep,
  totalSteps,
  highlightCurrentOnly,
  actions,
}: EnrollPictureScreenProps) {
  return (
    <>
      <img className={`${styles.hero} ${heroClassName ?? ''}`} src={heroSrc} alt={heroAlt ?? ''} />
      <button type="button" className={`${styles.closeX} ${styles.closeXLight}`} onClick={onClose} aria-label="Close">
        &times;
      </button>

      <div className={styles.body}>
        <h3 className={styles.title}>{title}</h3>
        <p className={styles.description}>{description}</p>
      </div>

      <div className={styles.footer}>
        <StepProgress
          currentStep={currentStep}
          totalSteps={totalSteps}
          hideLabel
          highlightCurrentOnly={highlightCurrentOnly}
        />
        <ModalActions>{actions}</ModalActions>
      </div>

      <div className={styles.support}>
        Have questions?{' '}
        <a href={`mailto:${SUPPORT_EMAIL}?subject=Fi%20Rescue%20Program%20Enrollment%20Questions`}>Contact support</a>
      </div>
    </>
  );
}
